import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Alert,
  Grid,
} from '@mui/material';
import { format, parse } from 'date-fns';
import { projectService } from '../../services/projectService';
import sblViewService from '../../services/sblViewService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#2E7D32',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #1B5E20',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const SBLView = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectName, setProjectName] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    loadProjects();
    loadDefaultRange();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const loadDefaultRange = async () => {
    try {
      const range = await sblViewService.getDefaultRange();
      setStartMonth(range.startMonth);
      setEndMonth(range.endMonth);
    } catch (err) {
      console.error('Failed to load default range:', err);
    }
  };

  const handleProjectChange = (e) => {
    const projectCode = e.target.value;
    setSelectedProject(projectCode);

    // Find and set project name
    const project = projects.find(p => p.project_code === projectCode);
    if (project) {
      setProjectName(project.name);
    } else {
      setProjectName('');
    }

    // Clear summary data when project changes
    setSummaryData(null);
  };

  const handleViewSummary = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    if (!startMonth || !endMonth) {
      setError('Please select month range');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data = await sblViewService.getSummary(
        selectedProject,
        startMonth,
        endMonth
      );

      setSummaryData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!summaryData || !summaryData.employees || summaryData.employees.length === 0) {
      setError('No data to export. Please load summary first.');
      return;
    }

    try {
      const numMonths = summaryData.months.length;

      // Build headers - SNo + All Days first, then All Values
      const headers = ['SNo', 'EMP ID', 'Employee Name', 'Project Code', 'Bill Rate', 'Location'];
      // Add all Days headers
      summaryData.months.forEach(month => {
        headers.push(`${formatMonthDisplay(month)} Days`);
      });
      headers.push('Total Days');
      // Add all Value headers
      summaryData.months.forEach(month => {
        headers.push(`${formatMonthDisplay(month)} Value`);
      });
      headers.push('Total Value');

      // Create styled header row
      const headerRow = headers.map((header, index) => {
        let bgColor = '2E7D32'; // Green default
        if (index === 4) bgColor = '1565C0'; // Blue for Bill Rate
        else if (index > 5 && index <= 6 + numMonths) {
          // Days columns (teal)
          const monthIndex = index - 6;
          bgColor = monthIndex % 2 === 0 ? '00897B' : '00796B';
        } else if (index === 6 + numMonths) {
          bgColor = '00695C'; // Total Days
        } else if (index > 6 + numMonths && index < headers.length - 1) {
          // Value columns (purple)
          const monthIndex = index - 7 - numMonths;
          bgColor = monthIndex % 2 === 0 ? '7B1FA2' : '9C27B0';
        } else if (index === headers.length - 1) {
          bgColor = '283593'; // Total Value
        }

        return {
          v: header,
          t: 's',
          s: {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
            fill: { fgColor: { rgb: bgColor } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
              top: { style: 'thin', color: { rgb: 'FFFFFF' } },
              bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
              left: { style: 'thin', color: { rgb: 'FFFFFF' } },
              right: { style: 'thin', color: { rgb: 'FFFFFF' } }
            }
          }
        };
      });

      // Create data rows with alternating colors
      const dataRows = summaryData.employees.map((emp, rowIndex) => {
        const bgColor = rowIndex % 2 === 0 ? 'FFFFFF' : 'E8F5E9';
        const rowData = [
          rowIndex + 1, // SNo
          emp.emp_id,
          emp.name,
          emp.project_code,
          emp.bill_rate,
          emp.location,
        ];
        // All Days first
        summaryData.months.forEach(month => {
          rowData.push(emp.monthly_days?.[month] || 0);
        });
        rowData.push(emp.total_days || 0);
        // All Values
        summaryData.months.forEach(month => {
          rowData.push(emp.monthly_values?.[month] || 0);
        });
        rowData.push(emp.total_value || 0);

        return rowData.map((cell, colIndex) => {
          const isNumber = colIndex === 0 || colIndex === 4 || colIndex > 5;
          let cellBgColor = bgColor;

          // Total Days column background
          if (colIndex === 6 + numMonths) {
            cellBgColor = rowIndex % 2 === 0 ? 'E0F2F1' : 'B2DFDB';
          }
          // Total Value column background
          else if (colIndex === rowData.length - 1) {
            cellBgColor = rowIndex % 2 === 0 ? 'E8EAF6' : 'C5CAE9';
          }

          let textColor = '000000';
          if (colIndex === 1) textColor = '2E7D32'; // EMP ID
          else if (colIndex === 4) textColor = '1565C0'; // Bill Rate
          else if (colIndex > 5 && colIndex <= 6 + numMonths) textColor = '00695C'; // Days
          else if (colIndex > 6 + numMonths) textColor = colIndex === rowData.length - 1 ? '283593' : '7B1FA2'; // Values

          const isDayCol = colIndex > 5 && colIndex <= 6 + numMonths;
          const isValueCol = colIndex > 6 + numMonths;

          return {
            v: cell,
            t: isNumber ? 'n' : 's',
            s: {
              font: {
                color: { rgb: textColor },
                bold: colIndex === 0 || colIndex === 1 || colIndex === 6 + numMonths || colIndex === rowData.length - 1,
                sz: 9
              },
              fill: { fgColor: { rgb: cellBgColor } },
              alignment: { horizontal: colIndex === 0 ? 'center' : (isDayCol ? 'center' : (isNumber ? 'right' : 'left')), vertical: 'center' },
              numFmt: colIndex === 4 ? '$#,##0.00' : (isValueCol ? '$#,##0.00' : (isDayCol || colIndex === 0 ? '0' : undefined)),
              border: {
                top: { style: 'thin', color: { rgb: 'E0E0E0' } },
                bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
                left: { style: 'thin', color: { rgb: 'E0E0E0' } },
                right: { style: 'thin', color: { rgb: 'E0E0E0' } }
              }
            }
          };
        });
      });

      // Create grand totals row
      const grandTotalDays = calculateGrandTotalDays();
      const grandTotalValue = calculateGrandTotalValue();

      const totalsData = ['', 'GRAND TOTAL', '', '', '', ''];
      // All Days totals first
      summaryData.months.forEach(month => {
        const monthDaysTotal = summaryData.employees.reduce((sum, emp) => sum + (emp.monthly_days?.[month] || 0), 0);
        totalsData.push(monthDaysTotal);
      });
      totalsData.push(grandTotalDays);
      // All Value totals
      summaryData.months.forEach(month => {
        const monthValueTotal = summaryData.employees.reduce((sum, emp) => sum + (emp.monthly_values?.[month] || 0), 0);
        totalsData.push(monthValueTotal);
      });
      totalsData.push(grandTotalValue);

      const totalsRow = totalsData.map((cell, colIndex) => {
        const isNumber = colIndex > 5;
        let bgColor = '2E7D32';
        if (colIndex > 5 && colIndex < 6 + numMonths) bgColor = '00796B'; // Days
        else if (colIndex === 6 + numMonths) bgColor = '00695C'; // Total Days
        else if (colIndex > 6 + numMonths && colIndex < totalsData.length - 1) bgColor = '7B1FA2'; // Values
        else if (colIndex === totalsData.length - 1) bgColor = '283593'; // Total Value

        const isDayCol = colIndex > 5 && colIndex <= 6 + numMonths;
        const isValueCol = colIndex > 6 + numMonths;

        return {
          v: cell,
          t: isNumber ? 'n' : 's',
          s: {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
            fill: { fgColor: { rgb: bgColor } },
            alignment: { horizontal: isDayCol ? 'center' : (isNumber ? 'right' : 'left'), vertical: 'center' },
            numFmt: isValueCol ? '$#,##0.00' : (isDayCol ? '0' : undefined),
            border: {
              top: { style: 'thin', color: { rgb: 'FFFFFF' } },
              bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
              left: { style: 'thin', color: { rgb: 'FFFFFF' } },
              right: { style: 'thin', color: { rgb: 'FFFFFF' } }
            }
          }
        };
      });

      // Build worksheet
      const wsData = [headerRow, ...dataRows, totalsRow];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 5 },  // SNo
        { wch: 12 }, // EMP ID
        { wch: 22 }, // Employee Name
        { wch: 12 }, // Project Code
        { wch: 10 }, // Bill Rate
        { wch: 12 }, // Location
      ];
      // Days columns
      summaryData.months.forEach(() => colWidths.push({ wch: 8 }));
      colWidths.push({ wch: 10 }); // Total Days
      // Value columns
      summaryData.months.forEach(() => colWidths.push({ wch: 12 }));
      colWidths.push({ wch: 12 }); // Total Value
      ws['!cols'] = colWidths;

      // Set row heights
      ws['!rows'] = [{ hpt: 30 }]; // Header row height

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SBL View');

      const fileName = `SBL_View_${selectedProject}_${startMonth.substring(0, 7)}_${endMonth.substring(0, 7)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Excel export error:', err);
      setError('Failed to export Excel: ' + err.message);
    }
  };

  const handleDownloadPDF = () => {
    if (!summaryData || !summaryData.employees || summaryData.employees.length === 0) {
      setError('No data to export. Please load summary first.');
      return;
    }

    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const numMonths = summaryData.months.length;

      // Title
      doc.setFontSize(18);
      doc.setTextColor(46, 125, 50);
      doc.text('SBL View - Monthly Summary', 14, 15);

      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Project: ${selectedProject} - ${projectName}`, 14, 22);
      doc.text(`Period: ${formatMonthDisplay(startMonth)} to ${formatMonthDisplay(endMonth)}`, 14, 28);

      // Build headers - SNo + All Days first, then All Values
      const headers = [['SNo', 'EMP ID', 'Name', 'Project', 'Rate', 'Location']];
      // All Days headers
      summaryData.months.forEach(month => {
        headers[0].push(`${formatMonthDisplay(month)} Days`);
      });
      headers[0].push('Tot Days');
      // All Value headers
      summaryData.months.forEach(month => {
        headers[0].push(`${formatMonthDisplay(month)} Val`);
      });
      headers[0].push('Tot Value');

      // Build data rows
      const data = summaryData.employees.map((emp, idx) => {
        const rowData = [
          (idx + 1).toString(), // SNo
          emp.emp_id,
          emp.name,
          emp.project_code,
          `$${emp.bill_rate}`,
          emp.location,
        ];
        // All Days first
        summaryData.months.forEach(month => {
          const days = emp.monthly_days?.[month] || 0;
          rowData.push(days > 0 ? days.toString() : '-');
        });
        rowData.push((emp.total_days || 0).toString());
        // All Values
        summaryData.months.forEach(month => {
          const val = emp.monthly_values?.[month] || 0;
          rowData.push(val > 0 ? `$${val.toFixed(0)}` : '-');
        });
        rowData.push(`$${(emp.total_value || 0).toFixed(2)}`);
        return rowData;
      });

      // Add grand totals row
      const grandTotalDays = calculateGrandTotalDays();
      const grandTotalValue = calculateGrandTotalValue();
      const totalsRow = ['', 'TOTAL', '', '', '', ''];
      // All Days totals first
      summaryData.months.forEach(month => {
        const monthDaysTotal = summaryData.employees.reduce((sum, emp) => sum + (emp.monthly_days?.[month] || 0), 0);
        totalsRow.push(monthDaysTotal > 0 ? monthDaysTotal.toString() : '-');
      });
      totalsRow.push(grandTotalDays.toString());
      // All Value totals
      summaryData.months.forEach(month => {
        const monthValueTotal = summaryData.employees.reduce((sum, emp) => sum + (emp.monthly_values?.[month] || 0), 0);
        totalsRow.push(monthValueTotal > 0 ? `$${monthValueTotal.toFixed(0)}` : '-');
      });
      totalsRow.push(`$${grandTotalValue.toFixed(2)}`);
      data.push(totalsRow);

      const totalRows = data.length;
      const totalCols = headers[0].length;
      const totalDaysColIndex = 6 + numMonths;
      const totalValueColIndex = totalCols - 1;

      autoTable(doc, {
        head: headers,
        body: data,
        startY: 34,
        styles: {
          fontSize: 6,
          cellPadding: 1.5,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 5.5,
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 8 }, // SNo
          1: { fontStyle: 'bold', textColor: [46, 125, 50], cellWidth: 16 }, // EMP ID
          2: { cellWidth: 26 }, // Name
          3: { cellWidth: 14 }, // Project
          4: { halign: 'right', textColor: [21, 101, 192], cellWidth: 11 }, // Rate
          5: { cellWidth: 16 }, // Location
        },
        didParseCell: function(hookData) {
          const colIndex = hookData.column.index;

          // Bill Rate column header
          if (hookData.section === 'head' && colIndex === 4) {
            hookData.cell.styles.fillColor = [21, 101, 192];
          }

          // Days columns (indices 6 to 6+numMonths-1)
          if (colIndex > 5 && colIndex < totalDaysColIndex) {
            const monthIndex = colIndex - 6;
            if (hookData.section === 'head') {
              hookData.cell.styles.fillColor = monthIndex % 2 === 0 ? [0, 137, 123] : [0, 121, 107];
            }
            if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
              hookData.cell.styles.halign = 'center';
              hookData.cell.styles.textColor = [0, 105, 92];
            }
          }

          // Total Days column
          if (colIndex === totalDaysColIndex) {
            if (hookData.section === 'head') {
              hookData.cell.styles.fillColor = [0, 105, 92];
            }
            if (hookData.section === 'body') {
              hookData.cell.styles.halign = 'center';
              hookData.cell.styles.fontStyle = 'bold';
              if (hookData.row.index < totalRows - 1) {
                hookData.cell.styles.textColor = [0, 105, 92];
                hookData.cell.styles.fillColor = hookData.row.index % 2 === 0 ? [224, 242, 241] : [178, 223, 219];
              }
            }
          }

          // Value columns (indices totalDaysColIndex+1 to totalValueColIndex-1)
          if (colIndex > totalDaysColIndex && colIndex < totalValueColIndex) {
            const monthIndex = colIndex - totalDaysColIndex - 1;
            if (hookData.section === 'head') {
              hookData.cell.styles.fillColor = monthIndex % 2 === 0 ? [123, 31, 162] : [156, 39, 176];
            }
            if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
              hookData.cell.styles.halign = 'right';
              hookData.cell.styles.textColor = [123, 31, 162];
            }
          }

          // Total Value column
          if (colIndex === totalValueColIndex) {
            if (hookData.section === 'head') {
              hookData.cell.styles.fillColor = [40, 53, 147];
            }
            if (hookData.section === 'body') {
              hookData.cell.styles.halign = 'right';
              hookData.cell.styles.fontStyle = 'bold';
              if (hookData.row.index < totalRows - 1) {
                hookData.cell.styles.textColor = [40, 53, 147];
                hookData.cell.styles.fillColor = hookData.row.index % 2 === 0 ? [232, 234, 246] : [197, 202, 233];
              }
            }
          }

          // Alternate row colors for basic columns
          if (hookData.section === 'body' && hookData.row.index < totalRows - 1) {
            if (colIndex <= 5) {
              hookData.cell.styles.fillColor = hookData.row.index % 2 === 0 ? [255, 255, 255] : [232, 245, 233];
            }
          }

          // Grand totals row styling
          if (hookData.section === 'body' && hookData.row.index === totalRows - 1) {
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.textColor = [255, 255, 255];
            if (colIndex <= 5) {
              hookData.cell.styles.fillColor = [46, 125, 50];
            } else if (colIndex < totalDaysColIndex) {
              hookData.cell.styles.fillColor = [0, 121, 107];
              hookData.cell.styles.halign = 'center';
            } else if (colIndex === totalDaysColIndex) {
              hookData.cell.styles.fillColor = [0, 105, 92];
              hookData.cell.styles.halign = 'center';
            } else if (colIndex < totalValueColIndex) {
              hookData.cell.styles.fillColor = [123, 31, 162];
              hookData.cell.styles.halign = 'right';
            } else {
              hookData.cell.styles.fillColor = [40, 53, 147];
              hookData.cell.styles.halign = 'right';
            }
          }
        },
      });

      const fileName = `SBL_View_${selectedProject}_${startMonth.substring(0, 7)}_${endMonth.substring(0, 7)}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF: ' + err.message);
    }
  };

  const formatMonthDisplay = (monthStr) => {
    try {
      const date = parse(monthStr, 'yyyy-MM-dd', new Date());
      return format(date, 'MMM-yy');
    } catch {
      return monthStr;
    }
  };

  const calculateGrandTotalValue = () => {
    if (!summaryData || !summaryData.employees) return 0;
    return summaryData.employees.reduce((sum, emp) => sum + (emp.total_value || 0), 0);
  };

  const calculateGrandTotalDays = () => {
    if (!summaryData || !summaryData.employees) return 0;
    return summaryData.employees.reduce((sum, emp) => sum + (emp.total_days || 0), 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        mt: 2,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(46, 125, 50, 0.3)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ViewWeekIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">SBL View</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Month-wise billable value summary with export options
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Project Code *"
              value={selectedProject}
              onChange={handleProjectChange}
              required
            >
              <MenuItem value="">-- Select Project --</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.project_code} value={project.project_code}>
                  {project.project_code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Project Name"
              value={projectName}
              InputProps={{
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Start Month"
              type="month"
              value={startMonth ? startMonth.substring(0, 7) : ''}
              onChange={(e) => setStartMonth(e.target.value + '-01')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="End Month"
              type="month"
              value={endMonth ? endMonth.substring(0, 7) : ''}
              onChange={(e) => setEndMonth(e.target.value + '-01')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleViewSummary}
              disabled={!selectedProject || loading}
              startIcon={<VisibilityIcon />}
            >
              View Summary
            </Button>
          </Grid>
        </Grid>

        {/* Export Buttons */}
        {summaryData && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadExcel}
              disabled={loading}
              startIcon={<TableChartIcon />}
            >
              Download Excel
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDownloadPDF}
              disabled={loading}
              startIcon={<PictureAsPdfIcon />}
            >
              Download PDF
            </Button>
          </Box>
        )}
      </Paper>

      {/* Summary Table */}
      {summaryData && (
        <Paper sx={{ mt: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 0, zIndex: 3, minWidth: 50 }} align="center">
                    SNo
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 50, zIndex: 3 }}>
                    EMP ID
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, position: 'sticky', left: 130, zIndex: 3 }}>
                    Employee Name
                  </TableCell>
                  <TableCell sx={headerCellStyle}>
                    Project Code
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }} align="right">
                    Bill Rate
                  </TableCell>
                  <TableCell sx={headerCellStyle}>
                    Location
                  </TableCell>

                  {/* All Billable Days columns first */}
                  {summaryData.months && summaryData.months.map((month, index) => (
                    <TableCell
                      key={`days-${month}`}
                      sx={{
                        ...headerCellStyle,
                        backgroundColor: index % 2 === 0 ? '#00897B' : '#00796B',
                        borderBottom: index % 2 === 0 ? '3px solid #004D40' : '3px solid #00695C',
                        minWidth: 70
                      }}
                      align="center"
                    >
                      {formatMonthDisplay(month)}<br/>Days
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...headerCellStyle, backgroundColor: '#00695C', borderBottom: '3px solid #004D40', minWidth: 80 }} align="center">
                    Total<br/>Days
                  </TableCell>

                  {/* All Billable Value columns */}
                  {summaryData.months && summaryData.months.map((month, index) => (
                    <TableCell
                      key={`value-${month}`}
                      sx={{
                        ...headerCellStyle,
                        backgroundColor: index % 2 === 0 ? '#7B1FA2' : '#9C27B0',
                        borderBottom: index % 2 === 0 ? '3px solid #4A148C' : '3px solid #6A1B9A',
                        minWidth: 100
                      }}
                      align="right"
                    >
                      {formatMonthDisplay(month)}<br/>Value
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...headerCellStyle, backgroundColor: '#283593', borderBottom: '3px solid #1A237E', minWidth: 120 }} align="right">
                    Total<br/>Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.employees && summaryData.employees.length > 0 ? (
                  <>
                    {summaryData.employees.map((employee, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#E8F5E9',
                          '&:hover': { backgroundColor: '#C8E6C9' },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell align="center" sx={{ position: 'sticky', left: 0, backgroundColor: index % 2 === 0 ? '#ffffff' : '#E8F5E9', zIndex: 2 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ position: 'sticky', left: 50, backgroundColor: index % 2 === 0 ? '#ffffff' : '#E8F5E9', zIndex: 2, fontWeight: 'bold' }}>
                          {employee.emp_id}
                        </TableCell>
                        <TableCell sx={{ position: 'sticky', left: 130, backgroundColor: index % 2 === 0 ? '#ffffff' : '#E8F5E9', zIndex: 2 }}>
                          {employee.name}
                        </TableCell>
                        <TableCell>{employee.project_code}</TableCell>
                        <TableCell align="right" sx={{ color: '#1565C0', fontWeight: 'bold' }}>${employee.bill_rate}/hr</TableCell>
                        <TableCell>{employee.location}</TableCell>

                        {/* All Billable Days first */}
                        {summaryData.months && summaryData.months.map((month) => (
                          <TableCell key={`days-${month}`} align="center" sx={{ color: '#00695C', fontWeight: 500 }}>
                            {employee.monthly_days?.[month] > 0
                              ? employee.monthly_days[month]
                              : '-'}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: index % 2 === 0 ? '#E0F2F1' : '#B2DFDB', color: '#00695C' }}>
                          {employee.total_days || 0}
                        </TableCell>

                        {/* All Billable Values */}
                        {summaryData.months && summaryData.months.map((month) => (
                          <TableCell key={`value-${month}`} align="right" sx={{ color: '#7B1FA2' }}>
                            {employee.monthly_values?.[month] > 0
                              ? `$${employee.monthly_values[month].toFixed(2)}`
                              : '-'}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: index % 2 === 0 ? '#E8EAF6' : '#C5CAE9', color: '#283593' }}>
                          ${(employee.total_value || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Grand Total Row */}
                    <TableRow sx={{ backgroundColor: '#2E7D32' }}>
                      <TableCell colSpan={6} sx={{ fontWeight: 'bold', fontSize: '1.1rem', position: 'sticky', left: 0, backgroundColor: '#2E7D32', color: 'white' }}>
                        GRAND TOTAL
                      </TableCell>
                      {/* All Days Totals first */}
                      {summaryData.months && summaryData.months.map((month) => {
                        const monthDaysTotal = summaryData.employees.reduce(
                          (sum, emp) => sum + (emp.monthly_days?.[month] || 0),
                          0
                        );
                        return (
                          <TableCell key={`days-${month}`} align="center" sx={{ fontWeight: 'bold', color: 'white', backgroundColor: '#00796B' }}>
                            {monthDaysTotal > 0 ? monthDaysTotal : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white', backgroundColor: '#00695C' }}>
                        {calculateGrandTotalDays()}
                      </TableCell>
                      {/* All Value Totals */}
                      {summaryData.months && summaryData.months.map((month) => {
                        const monthValueTotal = summaryData.employees.reduce(
                          (sum, emp) => sum + (emp.monthly_values?.[month] || 0),
                          0
                        );
                        return (
                          <TableCell key={`value-${month}`} align="right" sx={{ fontWeight: 'bold', color: 'white', backgroundColor: '#7B1FA2' }}>
                            {monthValueTotal > 0 ? `$${monthValueTotal.toFixed(2)}` : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white', backgroundColor: '#283593' }}>
                        ${calculateGrandTotalValue().toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={100} align="center">
                      No data available for the selected project and date range
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default SBLView;
